<script lang="ts">
import {
    ref as moduleRef,
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";

// Играющее сейчас видеосообщение (url вложения) — модульное: правило «играет
// только одно», старт нового сворачивает предыдущее в любом инстансе.
const activeVideoNote = moduleRef<string | null>(null);

// Волны старых аудио без waveform в метаданных: декодим блоб один раз при
// первом воспроизведении, кэшируем по URL вложения (живёт сквозь ремонты).
const decodedWaveforms = new Map<string, number[]>();
</script>

<script setup lang="ts">
import { decodeWaveform } from "@shared/lib/audioWaveform";

import AudioPlayerBar from "@features/audio-player/AudioPlayerBar.vue";
import {
    formatTime,
    useAudioPlayer,
} from "@features/audio-player/useAudioPlayer";
import type { DecryptedMessage } from "@features/chat-session/model/useChatSession";
import FileChip from "@features/file-transfer/FileChip.vue";
import ImageLightbox from "@features/file-transfer/ImageLightbox.vue";
import { useFileDownload } from "@features/file-transfer/useFileDownload";
import { useMediaSource } from "@features/file-transfer/useMediaSource";
import VideoPlayerModal from "@features/file-transfer/VideoPlayerModal.vue";

import MessageMeta from "./MessageMeta.vue";

import type { FileAttachment } from "shared";

const props = defineProps<{
    file: FileAttachment;
    // Для меты внутри аудиоплеера (сообщение без текста).
    msg?: DecryptedMessage;
    isSelf?: boolean;
    showMeta?: boolean;
}>();

const { downloadFile } = useFileDownload();
const { get: getMedia, load: loadMedia } = useMediaSource();

const isImage = computed(() => props.file.mimeType.startsWith("image/"));
const isVideo = computed(() => props.file.mimeType.startsWith("video/"));
const isAudio = computed(() => props.file.mimeType.startsWith("audio/"));
const isMedia = computed(() => isImage.value || isVideo.value || isAudio.value);
// Видеосообщение (записано в чате): уменьшенный квадрат, по клику играет инлайн.
const isVideoNote = computed(
    () => isVideo.value && props.file.kind === "video-note"
);

// Состояние медиа (objectURL + прогресс) — модульное, по URL вложения: virtua
// пересоздаёт компоненты при скролле, и локальный стейт терялся бы на каждом
// ремонте (видео «прогружалось» бы вечно заново).
const media = getMedia(props.file);
const objectUrl = computed(() => media.url);
const mediaProgress = computed(() => media.progress);
const mediaError = computed(() => media.error);

// Превью ленты — постер из метаданных: генерирует отправитель, приходит вместе
// с сообщением, файл ради него качать не нужно.
const preview = computed(() => props.file.poster);

// Фиксируем габариты превью из метаданных вложения (width/height пишутся при
// загрузке). Без этого <img> после ремонта virtua первые кадры нулевой высоты —
// лента мерила «низкий» элемент, высота доезжала после декода, скролл прыгал.
const PREVIEW_MAX_W = 420;
const PREVIEW_MAX_H = 360;
const previewDims = computed(() => {
    const { width, height } = props.file;
    if (!width || !height) {
        return undefined;
    }
    const scale = Math.min(PREVIEW_MAX_W / width, PREVIEW_MAX_H / height, 1);
    return {
        width: Math.round(width * scale),
        height: Math.round(height * scale),
    };
});
const aspectStyle = computed(() =>
    previewDims.value
        ? {
              width: `${previewDims.value.width}px`,
              height: `${previewDims.value.height}px`,
          }
        : undefined
);

function ensureMedia() {
    void loadMedia(props.file);
}

// Автоподгрузка по вьюпорту нужна только сообщениям БЕЗ постера в метаданных
// (постер показывается сразу, полный файл качаем по клику).
const rootEl = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

onMounted(() => {
    if (!rootEl.value || !isMedia.value || media.url || props.file.poster) {
        return;
    }
    observer = new IntersectionObserver(
        (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                observer?.disconnect();
                observer = null;
                ensureMedia();
            }
        },
        { rootMargin: "200px" }
    );
    observer.observe(rootEl.value);
});

onBeforeUnmount(() => {
    observer?.disconnect();
    // Ушли из вьюпорта (virtua размонтировал) — останавливаем своё видео,
    // чтобы при возврате оно не «играло» заново с нуля.
    if (activeVideoNote.value === props.file.url) {
        activeVideoNote.value = null;
    }
});

// --- Аудио: глобальный плеер (один трек на всё приложение). Клик по плей
// докачивает блоб и отдаёт трек в общий контекст — сверху появится глобальная
// полоса, пузырь и полоса читают один стейт.
const player = useAudioPlayer();
const isCurrentAudio = computed(
    () => player.state.track?.id === props.file.url
);
const audioProgress = computed(() =>
    isCurrentAudio.value && player.state.duration > 0
        ? player.state.time / player.state.duration
        : 0
);
const audioTimeLabel = computed(() => {
    if (media.loading) {
        return `${Math.round(mediaProgress.value * 100)}%`;
    }
    if (isCurrentAudio.value) {
        return `${formatTime(player.state.time)} / ${formatTime(player.state.duration)}`;
    }
    return formatTime(props.file.duration ?? 0);
});

// Волна: из метаданных вложения; у старых сообщений без waveform — декод
// блоба при первом воспроизведении.
const decodedPeaks = ref<number[] | null>(
    decodedWaveforms.get(props.file.url) ?? null
);
const audioPeaks = computed(() =>
    props.file.waveform?.length
        ? props.file.waveform
        : (decodedPeaks.value ?? undefined)
);

async function ensureWaveform(): Promise<void> {
    if (audioPeaks.value || !media.url) {
        return;
    }
    const decoded = await decodeWaveform(media.url);
    if (decoded) {
        decodedWaveforms.set(props.file.url, decoded.peaks);
        decodedPeaks.value = decoded.peaks;
    }
}

async function onAudioToggle(): Promise<void> {
    if (isCurrentAudio.value) {
        player.toggle();
        return;
    }
    await loadMedia(props.file);
    if (media.url) {
        void ensureWaveform();
        player.play({
            id: props.file.url,
            src: media.url,
            title:
                props.file.kind === "voice"
                    ? "Голосовое сообщение"
                    : props.file.name,
            durationHint: props.file.duration,
        });
    }
}

function onAudioSeek(ratio: number): void {
    if (isCurrentAudio.value) {
        player.seek(ratio);
    } else {
        // Ещё не текущий трек — стартуем с начала.
        void onAudioToggle();
    }
}

// Лайтбокс картинки (ImageLightbox сам грузит оригинал и показывает превью).
const lightbox = ref(false);
function openLightbox() {
    lightbox.value = true;
}

// Бейдж длительности на постере (из метаданных отправителя).
const durationLabel = computed(() => {
    const d = props.file.duration;
    if (!d || !Number.isFinite(d)) {
        return null;
    }
    const m = Math.floor(d / 60);
    const s = Math.round(d % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
});

// Клик по постеру видео: открываем модалку с проигрывателем (VideoPlayerModal
// сам решает, стримить через SW или качать блоб). В ленте — только постер.
const playerOpen = ref(false);
function openPlayer() {
    playerOpen.value = true;
}

// --- Видеосообщение: свернуто — маленький квадрат-постер, по клику качается
// (блоб через общий кэш), разворачивается до исходного размера и играет инлайн.
// Играет только одно (модульный activeVideoNote): старт нового сворачивает
// предыдущее.
const NOTE_SIZE = 300;
const notePlaying = computed(() => activeVideoNote.value === props.file.url);

const noteExpandedSize = computed(() =>
    Math.min(previewDims.value?.width ?? 320, previewDims.value?.height ?? 320)
);
const noteStyle = computed(() => {
    const side =
        notePlaying.value && objectUrl.value
            ? noteExpandedSize.value
            : NOTE_SIZE;
    return { width: `${side}px`, height: `${side}px` };
});

async function onNoteClick() {
    if (notePlaying.value) {
        return;
    }
    await loadMedia(props.file);
    if (media.url) {
        noteProgress.value = 0;
        activeVideoNote.value = props.file.url;
    }
}

// Клик по играющему (и конец ролика) — сворачиваемся к исходному постеру.
function onNoteEnded() {
    if (activeVideoNote.value === props.file.url) {
        activeVideoNote.value = null;
    }
}

// Прогресс воспроизведения — рамка по периметру квадрата (как в модалке записи).
const noteProgress = ref(0);

function onNoteTime(e: Event) {
    const video = e.target as HTMLVideoElement;
    noteProgress.value =
        Number.isFinite(video.duration) && video.duration > 0
            ? video.currentTime / video.duration
            : 0;
}

// Блок в потоке растёт ВНИЗ, и низ квадрата уезжал бы за край вьюпорта.
// Компенсируем: на каждый кадр анимации прибавляем к scrollTop прирост высоты —
// нижняя кромка стоит на месте, квадрат визуально растёт вверх (сворачивание —
// симметрично, дельта отрицательная).
function findScrollParent(el: HTMLElement): HTMLElement | null {
    let p = el.parentElement;
    while (p) {
        const s = getComputedStyle(p);
        if (/(auto|scroll)/.test(s.overflowY)) {
            return p;
        }
        p = p.parentElement;
    }
    return null;
}

function compensateNoteScroll() {
    const el = rootEl.value;
    if (!el) {
        return;
    }
    const scroller = findScrollParent(el);
    if (!scroller) {
        return;
    }
    let prev = el.offsetHeight;
    const start = performance.now();
    const step = () => {
        const h = el.offsetHeight;
        scroller.scrollTop += h - prev;
        prev = h;
        // Чуть дольше transition (0.2s) — дожимаем последние кадры.
        if (performance.now() - start < 280) {
            requestAnimationFrame(step);
        }
    };
    requestAnimationFrame(step);
}

watch(notePlaying, () => compensateNoteScroll());

// Страховка: если после разворота верх всё же вышел за экран (сообщение у самого
// верха вьюпорта) — доводим минимальным скроллом.
function onNoteTransitionEnd(e: TransitionEvent) {
    if (e.propertyName !== "height" || !notePlaying.value) {
        return;
    }
    rootEl.value?.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

// Скачивание файла-карточки (не медиа) с прогрессом.
const dlStatus = ref<"idle" | "downloading" | "error">("idle");
const dlProgress = ref(0);

async function onDownload() {
    if (dlStatus.value === "downloading") {
        return;
    }
    dlStatus.value = "downloading";
    dlProgress.value = 0;
    try {
        await downloadFile(props.file, (f) => {
            dlProgress.value = f;
        });
        dlStatus.value = "idle";
    } catch {
        dlStatus.value = "error";
    }
}
</script>

<template>
    <div ref="rootEl" class="mc-attach">
        <!-- Картинка: в ленте — миниатюра (полноразмер декодировался бы заново
             на каждом ремонте virtua), оригинал — в лайтбоксе. Контейнер держит
             аспект из метаданных — высота не прыгает при подгрузке. -->
        <template v-if="isImage">
            <button
                v-if="preview || objectUrl"
                class="mc-attach__image"
                type="button"
                :style="aspectStyle"
                @click.stop="openLightbox"
            >
                <img
                    :src="preview ?? objectUrl ?? undefined"
                    :alt="file.name"
                    :width="previewDims?.width"
                    :height="previewDims?.height"
                    decoding="async"
                    draggable="false"
                />
            </button>
            <div
                v-else
                class="mc-attach__placeholder"
                :style="aspectStyle"
                @click.stop="ensureMedia"
            >
                <span v-if="mediaError">Не удалось загрузить</span>
                <span v-else>{{ Math.round(mediaProgress * 100) }}%</span>
            </div>
        </template>

        <!-- Видеосообщение: маленький квадрат-постер; клик — скачивание,
             разворот до исходного размера и инлайн-проигрывание. -->
        <template v-else-if="isVideoNote">
            <div
                class="mc-attach__note"
                :style="noteStyle"
                @click.stop="onNoteClick"
                @transitionend="onNoteTransitionEnd"
            >
                <video
                    v-if="notePlaying && objectUrl"
                    class="mc-attach__note-video"
                    :src="objectUrl"
                    autoplay
                    playsinline
                    @click.stop="onNoteEnded"
                    @ended="onNoteEnded"
                    @timeupdate="onNoteTime"
                />
                <!-- Рамка-прогресс по периметру (pathLength=100 → офсет
                         в процентах). -->
                <svg
                    v-if="notePlaying && objectUrl"
                    class="mc-attach__note-ring"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                >
                    <rect
                        x="0"
                        y="0"
                        width="100"
                        height="100"
                        pathLength="100"
                        :stroke-dashoffset="
                            100 - Math.min(1, noteProgress) * 100
                        "
                    />
                </svg>
                <template v-else>
                    <img
                        v-if="preview"
                        :src="preview"
                        :alt="file.name"
                        class="mc-attach__poster-img"
                        decoding="async"
                        draggable="false"
                    />
                    <span v-if="durationLabel" class="mc-attach__duration">
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <rect x="2" y="6" width="13" height="12" rx="2" />
                            <path d="M15 10l7-3v10l-7-3" />
                        </svg>
                        {{ durationLabel }}
                    </span>
                    <span class="mc-attach__play">
                        <span
                            v-if="mediaProgress > 0 && mediaProgress < 1"
                            class="mc-attach__play-progress"
                        >
                            {{ Math.round(mediaProgress * 100) }}%
                        </span>
                        <svg
                            v-else
                            width="22"
                            height="22"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                        >
                            <path
                                d="M19.376 12.4161L8.77735 19.4818C8.54759 19.635 8.23715 19.5729 8.08397 19.3432C8.02922 19.261 8 19.1645 8 19.0658V4.93433C8 4.65818 8.22386 4.43433 8.5 4.43433C8.59871 4.43433 8.69522 4.46355 8.77735 4.5183L19.376 11.584C19.6057 11.7372 19.6678 12.0477 19.5146 12.2774C19.478 12.3323 19.4309 12.3795 19.376 12.4161Z"
                            ></path>
                        </svg>
                    </span>
                </template>
            </div>
        </template>

        <!-- Видео: в ленте ТОЛЬКО картинка-постер (лёгкий <img>, virtua ремонты
             дешёвые). Клик открывает модалку с проигрывателем. -->
        <template v-else-if="isVideo">
            <button
                v-if="preview || objectUrl"
                class="mc-attach__poster"
                type="button"
                :style="aspectStyle"
                @click.stop="openPlayer"
            >
                <img
                    v-if="preview"
                    :src="preview"
                    :alt="file.name"
                    class="mc-attach__poster-img"
                    :width="previewDims?.width"
                    :height="previewDims?.height"
                    decoding="async"
                    draggable="false"
                />
                <!-- Длительность — сверху слева, тёмная плашка. -->
                <span v-if="durationLabel" class="mc-attach__duration">
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <rect x="2" y="6" width="13" height="12" rx="2" />
                        <path d="M15 10l7-3v10l-7-3" />
                    </svg>
                    {{ durationLabel }}
                </span>
                <!-- Пока видео открыто в модалке — play на постере прячем. -->
                <span v-if="!playerOpen" class="mc-attach__play">
                    <svg
                        width="22"
                        height="22"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                    >
                        <path
                            d="M19.376 12.4161L8.77735 19.4818C8.54759 19.635 8.23715 19.5729 8.08397 19.3432C8.02922 19.261 8 19.1645 8 19.0658V4.93433C8 4.65818 8.22386 4.43433 8.5 4.43433C8.59871 4.43433 8.69522 4.46355 8.77735 4.5183L19.376 11.584C19.6057 11.7372 19.6678 12.0477 19.5146 12.2774C19.478 12.3323 19.4309 12.3795 19.376 12.4161Z"
                        ></path>
                    </svg>
                </span>
            </button>
            <div
                v-else
                class="mc-attach__placeholder"
                :style="aspectStyle"
                @click.stop="openPlayer"
            >
                <span v-if="mediaError">Не удалось загрузить</span>
                <span v-else>{{ Math.round(mediaProgress * 100) }}%</span>
            </div>
        </template>

        <!-- Аудио: плеер в пузыре, трек играет в глобальном контексте -->
        <template v-else-if="isAudio">
            <AudioPlayerBar
                class="mc-attach__player"
                :playing="isCurrentAudio && player.state.playing"
                :progress="audioProgress"
                :time-label="audioTimeLabel"
                :peaks="audioPeaks"
                @toggle="onAudioToggle"
                @seek="onAudioSeek"
            >
                <template #meta>
                    <MessageMeta
                        v-if="showMeta && msg"
                        :msg="msg"
                        :is-self="!!isSelf"
                    />
                </template>
            </AudioPlayerBar>
            <span v-if="mediaError" class="mc-attach__player-error">
                Не удалось загрузить
            </span>
        </template>

        <!-- Остальное: карточка файла со скачиванием -->
        <button
            v-else
            type="button"
            class="mc-attach__file"
            @click.stop="onDownload"
        >
            <FileChip
                :name="file.name"
                :size="file.size"
                :mime-type="file.mimeType"
                :status="
                    dlStatus === 'downloading'
                        ? 'uploading'
                        : dlStatus === 'error'
                          ? 'error'
                          : 'done'
                "
                :progress="dlProgress"
                :removable="false"
            />
        </button>

        <!-- Лайтбокс картинки (превью в конечных габаритах, прогресс, ✕, Esc) -->
        <ImageLightbox
            :file="file"
            :visible="lightbox"
            @close="lightbox = false"
        />

        <!-- Модалка проигрывателя видео (SW-стрим или блоб — решает сама) -->
        <VideoPlayerModal
            :file="file"
            :visible="playerOpen"
            @close="playerOpen = false"
        />
    </div>
</template>

<style scoped>
.mc-attach {
    max-width: 420px;
    /* Запас для scrollIntoView при развороте видеосообщения: квадрат
       доскролливается не впритык к краю, а с отступом. */
    scroll-margin: 16px;
}

.mc-attach__image,
.mc-attach__file {
    display: block;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
}

.mc-attach__image {
    overflow: hidden;
}

.mc-attach__image img {
    display: block;
    /* Контейнер держит точный размер (aspectStyle) — картинка заполняет его. */
    width: 100%;
    height: 100%;
    max-width: 420px;
    max-height: 360px;
    object-fit: cover;
}

.mc-attach__poster {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 420px;
    padding: 0;
    border: none;
    background: #000;
    overflow: hidden;
    cursor: pointer;
}

.mc-attach__poster-img {
    display: block;
    /* Контейнер держит точный размер (aspectStyle) — кадр заполняет его. */
    width: 100%;
    height: 100%;
    max-width: 420px;
    max-height: 360px;
    object-fit: cover;
}

/* Видеосообщение: квадрат с плавным разворотом при проигрывании. */
.mc-attach__note {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
    overflow: hidden;
    cursor: pointer;
    transition:
        width 0.2s ease,
        height 0.2s ease;
}

.mc-attach__note-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* Прогресс воспроизведения — рамка по внутреннему периметру квадрата
   (как в модалке записи: pathLength=100, офсет в процентах). */
.mc-attach__note-ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.mc-attach__note-ring rect {
    fill: none;
    stroke: var(--mc-acid);
    /* В юнитах viewBox (квадрат ~360px → 1 юнит ≈ 3.6px): рисуется по кромке,
       внешняя половина клипается, видимая полоса ~4px. */
    stroke-width: 2.2;
    stroke-dasharray: 100;
    filter: drop-shadow(0 0 4px var(--mc-acid-glow));
    transition: stroke-dashoffset 0.25s linear;
}

.mc-attach__play-progress {
    font-family: var(--mc-mono);
    font-size: 11px;
}

.mc-attach__duration {
    position: absolute;
    top: 8px;
    left: 8px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 7px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-family: var(--mc-mono);
    font-size: 11px;
    line-height: 1.5;
}

/* Нейтральная кнопка: полупрозрачный тёмный круг, белый треугольник
   (в тон плашкам длительности/меты). */
.mc-attach__play {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    /* Оптическое центрирование треугольника в круге. */
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
    color: #fff;
    transition: background 0.12s ease;
}

.mc-attach__poster:hover .mc-attach__play {
    background: rgba(0, 0, 0, 0.65);
}

.mc-attach__player {
    width: 280px;
    max-width: 100%;
}

.mc-attach__player-error {
    display: block;
    margin-top: 4px;
    color: var(--mc-danger);
    font-family: var(--mc-mono);
    font-size: 11px;
}

.mc-attach__placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 220px;
    height: 140px;
    background: var(--mc-bg-input);
    border: 1px solid var(--mc-line);
    color: var(--mc-fg-mute);
    font-family: var(--mc-mono);
    font-size: 12px;
    cursor: pointer;
}
</style>
